import { useParams, Navigate, Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { getServiceBySlug, PATH_BY_VERTICAL } from "@/lib/content";
import { SERVICE_DETAILS } from "@/lib/serviceDetails";
import { SERVICE_FAQS } from "@/lib/serviceFaqs";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { ResumeUpload } from "@/components/common/ResumeUpload";
import { PageHeader } from "@/components/common/PageHeader";
import { Reveal } from "@/components/common/Reveal";
import { Icon } from "@/components/common/Icon";
import { TellUsButton } from "@/components/common/TellUsButton";

export default function ServiceDetail() {
  const { slug } = useParams();
  const found = getServiceBySlug(slug);
  if (!found) return <Navigate to="/services" replace />;

  const { service, vertical } = found;
  const detail = SERVICE_DETAILS[slug] || {};
  const path = PATH_BY_VERTICAL[vertical.id];
  const related = vertical.services.filter((s) => s.slug !== slug);
  const faqs = SERVICE_FAQS[slug] || [];

  return (
    <div data-testid="service-detail-page">
      <PageHeader
        overline={vertical.name}
        title={service.name}
        subtitle={detail.summary}
      />

      <section className="mx-auto max-w-7xl px-5 py-20 lg:px-8 lg:py-24">
        <div className="grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20">
          {/* Overview + outcomes */}
          <div>
            <Reveal>
              <p className="overline">Overview</p>
              <p className="mt-4 text-xl leading-relaxed text-slate-700">{detail.overview}</p>
              {detail.forWho && (
                <p className="mt-6 inline-flex items-center gap-2 rounded-full border border-border bg-slate-50 px-4 py-2 text-sm text-slate-600">
                  <span className="font-semibold text-slate-900">Best for:</span> {detail.forWho}
                </p>
              )}
            </Reveal>

            {detail.outcomes?.length > 0 && (
              <Reveal delay={0.1} className="mt-12">
                <h2 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">
                  What you can expect
                </h2>
                <ul className="mt-6 space-y-3">
                  {detail.outcomes.map((o) => (
                    <li key={o} className="flex items-start gap-3">
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-600 text-white">
                        <Check className="h-3.5 w-3.5" />
                      </span>
                      <span className="text-lg text-slate-700">{o}</span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            )}

            <Reveal delay={0.15} className="mt-12">
              <TellUsButton size="lg" path={path} testId="service-detail-cta" />
            </Reveal>
          </div>

          {/* What's included card */}
          {detail.included?.length > 0 && (
            <Reveal delay={0.1}>
              <div className="rounded-2xl border border-border bg-slate-950 p-8 text-white lg:sticky lg:top-24">
                <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-white/10 text-blue-400">
                  <Icon name={service.icon} className="h-6 w-6" />
                </span>
                <h2 className="mt-6 font-heading text-xl font-bold">What's included</h2>
                <ul className="mt-5 space-y-3">
                  {detail.included.map((i) => (
                    <li key={i} className="flex items-start gap-3 text-slate-300">
                      <Check className="mt-1 h-4 w-4 shrink-0 text-blue-400" />
                      <span>{i}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* Resume upload — Career services only */}
      {vertical.id === "career" && (
        <section className="border-t border-border bg-blue-50 py-20 lg:py-24" data-testid="service-resume-upload">
          <div className="mx-auto max-w-3xl px-5 lg:px-8">
            <Reveal>
              <p className="overline">Get started</p>
              <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">
                Share your resume
              </h2>
              <p className="mt-4 text-lg leading-relaxed text-slate-600">
                Upload your current resume and we'll use it to shape the right support for you.
                PDF or DOCX, up to 1 MB.
              </p>
            </Reveal>
            <Reveal delay={0.1} className="mt-8">
              <ResumeUpload source={`service:${slug}`} className="rounded-2xl border border-border bg-white p-6 lg:p-8" />
            </Reveal>
          </div>
        </section>
      )}

      {/* FAQs */}
      {faqs.length > 0 && (
        <section className="border-t border-border py-20 lg:py-24" data-testid="service-faqs">
          <div className="mx-auto max-w-3xl px-5 lg:px-8">
            <Reveal>
              <p className="overline">FAQ</p>
              <h2 className="mt-3 font-heading text-3xl font-extrabold tracking-tight text-slate-900 md:text-4xl">
                {service.name}, answered.
              </h2>
            </Reveal>
            <Reveal delay={0.1} className="mt-10">
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((f, i) => (
                  <AccordionItem key={f.q} value={f.q} data-testid={`faq-item-${i}`} className="border-border">
                    <AccordionTrigger
                      data-testid={`faq-trigger-${i}`}
                      className="py-5 text-left font-heading text-lg font-bold text-slate-900 hover:no-underline [&>svg]:text-blue-600"
                    >
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="pb-5 text-base leading-relaxed text-slate-600">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>
          </div>
        </section>
      )}

      {/* Related services */}
      {related.length > 0 && (
        <section className="border-t border-border bg-slate-50 py-20 lg:py-24">
          <div className="mx-auto max-w-7xl px-5 lg:px-8">
            <Reveal>
              <p className="overline">More in {vertical.short}</p>
              <h2 className="mt-3 font-heading text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl">
                Related services
              </h2>
            </Reveal>
            <div className="mt-10 grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-3">
              {related.map((s) => (
                <Reveal key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    data-testid={`related-service-${s.slug}`}
                    className="group flex h-full items-center justify-between gap-3 bg-white p-6 transition-colors hover:bg-blue-50"
                  >
                    <span className="flex items-center gap-3">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600">
                        <Icon name={s.icon} className="h-4.5 w-4.5" />
                      </span>
                      <span className="text-sm font-medium text-slate-800 group-hover:text-blue-700">{s.name}</span>
                    </span>
                    <ArrowUpRight className="h-4 w-4 text-slate-400 transition-transform duration-200 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-blue-600" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
